<script setup lang="ts">
import {ref, onMounted, onBeforeUnmount, watch, type PropType} from 'vue';

export type MeshState = 'active' | 'expired' | 'loading';

interface SpringValue {
	value: number;
	velocity: number;
	target: number;
}

interface MeshColor {
	r: number;
	g: number;
	b: number;
}

interface StateConfig {
	amplitude: number;
	frequency: number;
	speed: number;
	dotSize: number;
	waveType: 'calm' | 'sharp' | 'spiral' | 'pulse' | 'ripple' | 'noise' | 'breathe' | 'twist' | 'bounce' | 'terrain' | 'heartbeat' | 'wave' | 'vortex' | 'glitch' | 'diamond';
	color: MeshColor;
	rotationSpeed: number;
	deformation: number;
	offsetX: number;
	offsetY: number;
	opacity: number;
}

const STATE_CONFIGS: Record<MeshState, StateConfig> = {
	active: {
		amplitude: 4,
		frequency: 0.3,
		speed: 0.5,
		dotSize: 0.45,
		waveType: 'calm',
		color: {r: 200, g: 220, b: 255},
		rotationSpeed: 0,
		deformation: 0,
		offsetX: 0.7,
		offsetY: 0.95,
		opacity: 0.85,
	},
	expired: {
		amplitude: 10,
		frequency: 0.6,
		speed: 1.0,
		dotSize: 0.55,
		waveType: 'terrain',
		color: {r: 255, g: 180, b: 160},
		rotationSpeed: 0,
		deformation: 0.6,
		offsetX: 0.7,
		offsetY: 0.95,
		opacity: 0.85,
	},
	loading: {
		amplitude: 7,
		frequency: 0.5,
		speed: 1.2,
		dotSize: 0.45,
		waveType: 'spiral',
		color: {r: 220, g: 200, b: 255},
		rotationSpeed: 0.6,
		deformation: 0.3,
		offsetX: 0.5,
		offsetY: 0.65,
		opacity: 0.45,
	},
};

const LERP_SPEED = 0.02;

const GRID_COLS = 55;
const GRID_ROWS = 55;

const CAMERA_DISTANCE = 45;
const PERSPECTIVE = 120;
const TILT_X = 0.55;
const TILT_Y = 0.2;

const props = defineProps({
	state: {
		type: String as PropType<MeshState>,
		default: 'active',
	},
});

const canvasRef = ref<HTMLCanvasElement | null>(null);
let animationId: number | null = null;
let currentRotation = 0;
let pulseAmount = 0;

const ALL_WAVE_TYPES = [
	'calm', 'sharp', 'spiral', 'pulse', 'ripple', 'noise', 'breathe',
	'twist', 'bounce', 'terrain', 'heartbeat', 'wave', 'vortex', 'glitch', 'diamond',
] as const;

// Per-wave phase accumulators — only advance when wave is active
const wavePhases: Record<string, number> = Object.fromEntries(ALL_WAVE_TYPES.map(k => [k, 0]));

const springs: Record<string, SpringValue> = {};

function createSpring(key: string, initial: number): SpringValue {
	if (!springs[key]) {
		springs[key] = {value: initial, velocity: 0, target: initial};
	}
	return springs[key];
}

function updateSpring(spring: SpringValue): number {
	spring.value += (spring.target - spring.value) * LERP_SPEED;
	return spring.value;
}

function initSprings(state: MeshState) {
	const config = STATE_CONFIGS[state];
	const keys: (keyof StateConfig)[] = [
		'amplitude', 'frequency', 'speed', 'dotSize',
		'rotationSpeed', 'deformation', 'offsetX', 'offsetY', 'opacity',
	];
	for (const key of keys) {
		createSpring(key, config[key] as number);
	}
	createSpring('colorR', config.color.r);
	createSpring('colorG', config.color.g);
	createSpring('colorB', config.color.b);

	// waveType blend weights
	for (const wt of ALL_WAVE_TYPES) {
		createSpring(`wave_${wt}`, config.waveType === wt ? 1 : 0);
	}
}

function setTargets(state: MeshState) {
	const config = STATE_CONFIGS[state];

	springs['amplitude'].target = config.amplitude;
	springs['frequency'].target = config.frequency;
	springs['speed'].target = config.speed;
	springs['dotSize'].target = config.dotSize;
	springs['rotationSpeed'].target = config.rotationSpeed;
	springs['deformation'].target = config.deformation;
	springs['offsetX'].target = config.offsetX;
	springs['offsetY'].target = config.offsetY;
	springs['opacity'].target = config.opacity;
	springs['colorR'].target = config.color.r;
	springs['colorG'].target = config.color.g;
	springs['colorB'].target = config.color.b;

	for (const wt of ALL_WAVE_TYPES) {
		springs[`wave_${wt}`].target = config.waveType === wt ? 1 : 0;
	}
}

function updateAllSprings() {
	for (const key in springs) {
		updateSpring(springs[key]);
	}
}

// How settled the springs are (0 = transitioning, 1 = at rest)
function getTransitionSettled(): number {
	let maxDelta = 0;
	const settledKeys = ALL_WAVE_TYPES.map(wt => `wave_${wt}`);
	settledKeys.push('offsetX', 'offsetY');
	for (const key of settledKeys) {
		if (!springs[key]) continue;
		const s = springs[key];
		const delta = Math.abs(s.target - s.value);
		if (delta > maxDelta) maxDelta = delta;
	}
	return Math.max(0, 1 - maxDelta * 2);
}

function getWaveZ(
	x: number, y: number,
	amplitude: number,
	frequency: number,
	speed: number,
	deformation: number,
): number {
	// Squared weights — reduces crossover interference between waves
	const w: Record<string, number> = {};
	for (const wt of ALL_WAVE_TYPES) {
		w[wt] = springs[`wave_${wt}`].value ** 2;
	}

	let z = 0;
	const dist = Math.sqrt(x * x + y * y);
	const angle = Math.atan2(y, x);

	// Each wave uses its own phase accumulator
	const t: Record<string, number> = {};
	for (const wt of ALL_WAVE_TYPES) {
		t[wt] = wavePhases[wt] * speed;
	}

	// Calm — gentle flowing sine
	if (w.calm > 0.001) {
		z += w.calm * Math.sin(x * frequency + t.calm)
			* Math.cos(y * frequency * 0.7 + t.calm * 0.6);
	}

	// Sharp — aggressive, layered with noise
	if (w.sharp > 0.001) {
		const base = Math.sin(x * frequency * 1.5 + t.sharp)
			* Math.sin(y * frequency * 1.2 + t.sharp * 0.8);
		const n = Math.sin(x * 3.7 + y * 2.3 + t.sharp * 1.5) * deformation;
		z += w.sharp * (base + n);
	}

	// Spiral — rotating wave from center
	if (w.spiral > 0.001) {
		z += w.spiral * Math.sin(dist * frequency * 0.8 - t.spiral + angle * 2);
	}

	// Pulse — concentric rings expanding from center
	if (w.pulse > 0.001) {
		z += w.pulse * Math.sin(dist * frequency * 1.2 - t.pulse * 1.5)
			* Math.exp(-dist * 0.05);
	}

	// Ripple — water drop ripple, decaying outward
	if (w.ripple > 0.001) {
		const r1 = Math.sin(dist * frequency * 2 - t.ripple * 2) / (1 + dist * 0.3);
		const r2 = Math.sin(dist * frequency * 1.5 - t.ripple * 1.2 + 1.5) / (1 + dist * 0.2);
		z += w.ripple * (r1 + r2 * 0.5);
	}

	// Noise — chaotic pseudo-random movement
	if (w.noise > 0.001) {
		z += w.noise * (
			Math.sin(x * 2.3 + t.noise * 0.7)
			* Math.sin(y * 1.7 + t.noise * 0.9)
			+ Math.sin(x * 4.1 + y * 3.3 + t.noise * 1.1) * 0.5
			+ Math.cos(x * 1.1 - y * 2.7 + t.noise * 0.5) * 0.3
		);
	}

	// Breathe — slow uniform rise and fall
	if (w.breathe > 0.001) {
		const breath = Math.sin(t.breathe * 0.4);
		const subtle = Math.sin(x * frequency * 0.3 + y * frequency * 0.2) * 0.3;
		z += w.breathe * (breath + subtle);
	}

	// Twist — spiral distortion, like a whirlpool
	if (w.twist > 0.001) {
		const twistAngle = dist * frequency * 0.5 + t.twist;
		z += w.twist * Math.sin(x * Math.cos(twistAngle) + y * Math.sin(twistAngle));
	}

	// Bounce — wave traveling upward, like a bouncing ball
	if (w.bounce > 0.001) {
		const bounceVal = Math.abs(Math.sin(t.bounce * 0.8));
		z += w.bounce * bounceVal * Math.cos(y * frequency + x * frequency * 0.3);
	}

	// Terrain — layered landscape, multiple octaves
	if (w.terrain > 0.001) {
		z += w.terrain * (
			Math.sin(x * frequency * 0.5 + t.terrain * 0.3) * 0.5
			+ Math.sin(x * frequency * 1.1 + y * frequency * 0.8 + t.terrain * 0.2) * 0.3
			+ Math.sin(x * frequency * 2.3 + y * frequency * 1.7 + t.terrain * 0.15) * 0.2
		);
	}

	// Heartbeat — sharp pulse with pause
	if (w.heartbeat > 0.001) {
		const cycle = (t.heartbeat * 0.6) % (Math.PI * 2);
		const beat = Math.exp(-((cycle - 1) ** 2) * 4) + Math.exp(-((cycle - 1.8) ** 2) * 8) * 0.6;
		z += w.heartbeat * beat * Math.cos(dist * frequency * 0.4);
	}

	// Wave — ocean wave moving in one direction
	if (w.wave > 0.001) {
		z += w.wave * (
			Math.sin(x * frequency * 0.8 - t.wave) * 0.7
			+ Math.sin(x * frequency * 1.6 - t.wave * 1.3 + 0.5) * 0.3
		);
	}

	// Vortex — funnel pulling toward center
	if (w.vortex > 0.001) {
		const pull = Math.exp(-dist * 0.15);
		z += w.vortex * Math.sin(dist * frequency - t.vortex + angle * 3) * (1 + pull * 2);
	}

	// Glitch — periodic sharp row shifts
	if (w.glitch > 0.001) {
		const glitchRow = Math.sin(y * 5.0 + t.glitch * 2);
		const glitchTrigger = glitchRow > 0.7 ? 1 : 0;
		z += w.glitch * glitchTrigger * Math.sin(x * frequency * 3 + t.glitch);
	}

	// Diamond — rhombus-shaped waves using Manhattan distance
	if (w.diamond > 0.001) {
		const manhattan = Math.abs(x) + Math.abs(y);
		z += w.diamond * Math.sin(manhattan * frequency - t.diamond);
	}

	return z * amplitude;
}

function project(
	x: number, y: number, z: number,
): {sx: number; sy: number; scale: number} | null {
	const cosX = Math.cos(TILT_X);
	const sinX = Math.sin(TILT_X);
	const cosY = Math.cos(TILT_Y);
	const sinY = Math.sin(TILT_Y);

	// Rotate around X axis
	const y1 = y * cosX - z * sinX;
	const z1 = y * sinX + z * cosX;

	// Rotate around Y axis
	const x1 = x * cosY + z1 * sinY;
	const z2 = -x * sinY + z1 * cosY;

	const depth = CAMERA_DISTANCE + z2;
	if (depth <= 0) return null;

	const scale = PERSPECTIVE / depth;
	return {
		sx: x1 * scale,
		sy: y1 * scale,
		scale,
	};
}

function draw(ctx: CanvasRenderingContext2D, width: number, height: number) {
	ctx.clearRect(0, 0, width, height);

	updateAllSprings();
	const settled = getTransitionSettled();

	// Lerp pulse toward target
	pulseAmount += (pulseTarget - pulseAmount) * 0.06;
	if (Math.abs(pulseAmount) < 0.001) pulseAmount = 0;

	const amplitude = springs['amplitude'].value * (0.3 + 0.7 * settled) * (1 + pulseAmount * 0.5);
	const frequency = springs['frequency'].value;
	const speed = springs['speed'].value * (0.2 + 0.8 * settled);
	const dotSize = springs['dotSize'].value * (1 + pulseAmount * 0.3);
	const rotationSpeed = springs['rotationSpeed'].value;
	const deformation = springs['deformation'].value;
	const offsetX = springs['offsetX'].value;
	const offsetY = springs['offsetY'].value;
	const globalOpacity = springs['opacity'].value;
	const colorR = springs['colorR'].value;
	const colorG = springs['colorG'].value;
	const colorB = springs['colorB'].value;

	// Advance phase only for the target wave; fading waves freeze in place
	const dt = 0.016;
	for (const wt of ALL_WAVE_TYPES) {
		const s = springs[`wave_${wt}`];
		if (s.target > 0.5) {
			// Active wave — advance phase
			wavePhases[wt] += dt;
		}
		// Fading wave — phase frozen, it just dissolves in place
	}

	currentRotation += rotationSpeed * 0.016;
	const rotation = currentRotation;
	const cosR = Math.cos(rotation);
	const sinR = Math.sin(rotation);

	const spacing = 70 / Math.max(GRID_COLS, GRID_ROWS);
	const halfCols = (GRID_COLS - 1) / 2;
	const halfRows = (GRID_ROWS - 1) / 2;

	// Collect dots with depth for sorting
	const dots: {sx: number; sy: number; scale: number; alpha: number}[] = [];

	const circleRadius = halfCols * spacing * 0.99;

	for (let row = 0; row < GRID_ROWS; row++) {
		for (let col = 0; col < GRID_COLS; col++) {
			let gx = (col - halfCols) * spacing;
			let gy = (row - halfRows) * spacing;

			// Circular clip — clean edges, button handles final shape
			if (gx * gx + gy * gy > circleRadius * circleRadius) continue;

			// Apply rotation for loading state
			if (Math.abs(rotationSpeed) > 0.01) {
				const rx = gx * cosR - gy * sinR;
				const ry = gx * sinR + gy * cosR;
				gx = rx;
				gy = ry;
			}

			const gz = getWaveZ(gx, gy, amplitude, frequency, speed, deformation);

			const p = project(gx, gy, gz);
			if (!p) continue;

			p.sx += width * offsetX;
			p.sy += height * offsetY;

			// Radial fade from circle center
			const distNorm = Math.sqrt(gx * gx + gy * gy) / circleRadius;
			const radialFade = 1 - distNorm * distNorm;
			const alpha = radialFade * Math.min(1, p.scale * 0.8) * globalOpacity;

			dots.push({sx: p.sx, sy: p.sy, scale: p.scale, alpha});
		}
	}

	// Sort by depth (smaller scale = further away, draw first)
	dots.sort((a, b) => a.scale - b.scale);

	for (const dot of dots) {
		const radius = dotSize * dot.scale * 0.5;
		if (radius < 0.3) continue;

		ctx.beginPath();
		ctx.arc(dot.sx, dot.sy, radius, 0, Math.PI * 2);
		ctx.fillStyle = `rgba(${Math.round(colorR)}, ${Math.round(colorG)}, ${Math.round(colorB)}, ${dot.alpha * 0.85})`;
		ctx.fill();
	}
}

function startAnimation() {
	const canvas = canvasRef.value;
	if (!canvas) return;

	const ctx = canvas.getContext('2d');
	if (!ctx) return;

	const loop = () => {
		const rect = canvas.getBoundingClientRect();
		const dpr = window.devicePixelRatio || 1;
		const w = rect.width * dpr;
		const h = rect.height * dpr;

		if (canvas.width !== w || canvas.height !== h) {
			canvas.width = w;
			canvas.height = h;
		}

		ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		draw(ctx, rect.width, rect.height);

		animationId = requestAnimationFrame(loop);
	};

	loop();
}

function stopAnimation() {
	if (animationId !== null) {
		cancelAnimationFrame(animationId);
		animationId = null;
	}
}

watch(() => props.state, (newState) => {
	setTargets(newState);
});

onMounted(() => {
	initSprings(props.state);
	startAnimation();
});

onBeforeUnmount(() => {
	stopAnimation();
});

let pulseTarget = 0;

function pulse() {
	pulseTarget = 1;
}

function unpulse() {
	pulseTarget = 0;
}

defineExpose({ pulse, unpulse });
</script>

<template>
	<canvas ref="canvasRef" class="canvas-mesh-background"></canvas>
</template>

<style lang="scss">
.canvas-mesh-background {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	pointer-events: none;
	mix-blend-mode: plus-lighter;
	opacity: 0.5;
	border-radius: inherit;
}
</style>